import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trophy, Users, ChevronRight, Clock, Info, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { PageHeader, Spinner, Empty, Avatar } from "@/components/Common";
import { Countdown, Thumb } from "@/components/ContentGrid";

const ABSTRACT = [
  "https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
];

const FORMAT_NAMES: Record<string, string> = { quality: "Qualità per esposizione", most_liked: "Più Mi piace", most_commented: "Più commentato", most_saved: "Più salvato", curated: "Giuria" };

export function ContestCard({ c, i, compact = false }: any) {
  if (compact) return (
    <Link to={`/daily/${c.id}`} data-testid="contest-card" className="surface p-4 flex flex-col gap-3 hover:bg-white/60 min-h-[150px]">
      <div className="flex items-center gap-2 flex-wrap"><span className="chip">{c.category_name}</span>{c.format && c.format !== "quality" && <span className="chip" data-testid="contest-format-chip">{FORMAT_NAMES[c.format]}</span>}</div>
      <div className="font-semibold text-lg leading-tight">{c.title}</div>
      <div className="mt-auto flex items-center justify-between gap-3 text-sm text-ink/70">
        <span className="inline-flex items-center gap-1">Chiude tra <Countdown to={c.ends_at} className="font-semibold text-ink" /></span>
        <span className="inline-flex items-center gap-1"><Users size={14} />{c.entries}</span>
      </div>
      {c.my_entry && <span className="text-xs text-[hsl(var(--primary))] font-semibold">Partecipi già</span>}
    </Link>
  );
  return (
    <Link to={`/daily/${c.id}`} data-testid="contest-card" className={`relative overflow-hidden rounded-3xl border border-ink/10 hover:border-ink/20 shadow-[0_20px_50px_-30px_rgba(0,0,0,.9)] transition-[border-color,transform] duration-300 active:scale-[.99] group ${i === 0 ? "md:col-span-2 min-h-[300px]" : "min-h-[250px]"}`}>
      <img src={ABSTRACT[i % 2]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-[1.03] transition-[opacity,transform] duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(162_20%_88%)] via-[hsl(162_20%_88%/.82)] to-[hsl(162_20%_88%/.55)]" />
      <div className="relative h-full p-6 flex flex-col">
        <div className="flex justify-between items-start">
          <span className="chip">{c.category_name}</span>
          {c.format && c.format !== "quality" && <span className="chip" data-testid="contest-format-chip">{FORMAT_NAMES[c.format]}</span>}
          {c.extended && <span className="label text-primary">Prolungata +24h</span>}
        </div>
        <div className="mt-auto space-y-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">{c.title}</h2>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="label text-ink/62 mb-1">Si chiude tra</div>
              <Countdown to={c.ends_at} className="text-4xl sm:text-5xl font-bold" />
            </div>
            <div className="text-right text-sm text-ink/76">
              <div className="flex items-center gap-1 justify-end"><Users size={14} />{c.entries} iscritti</div>
              {c.my_entry ? <div className="text-primary font-semibold mt-1">Sei in gara</div> : <div className="mt-1 inline-flex items-center gap-1 font-semibold">Partecipa <ChevronRight size={14} /></div>}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function Daily() {
  const { data, isLoading } = useQuery({ queryKey: ["daily"], queryFn: () => api.get("/daily").then((r) => r.data) });
  return (
    <div data-testid="daily-page">
      <PageHeader kicker="Competizioni di oggi" title="UNSEEN Daily" right={<Link to="/regolamento" className="text-sm text-ink/70 hover:text-primary inline-flex items-center gap-1" data-testid="daily-rules-link"><Info size={14} />Regolamento</Link>} />
      {isLoading ? <div className="p-10"><Spinner /></div> : (
        <div className="px-5 md:px-10 py-8 space-y-14">
          <section className="grid md:grid-cols-2 gap-4" data-testid="open-contests">
            {data.open.length === 0 ? <Empty>Nessuna competizione aperta in questo momento.</Empty> : data.open.map((c: any, i: number) => <ContestCard key={c.id} c={c} i={i} />)}
          </section>
          <section>
            <h2 className="label text-ink/62 mb-4">Vincitori passati</h2>
            {data.past.length === 0 ? <Empty testId="past-winners-empty">Ancora nessuna gara conclusa. Il primo badge può essere tuo.</Empty> : (
              <div className="divide-y divide-ink/10 border border-[hsl(var(--border))] rounded-2xl overflow-hidden bg-[hsl(var(--surface))]" data-testid="past-winners">
                {data.past.map((c: any) => (
                  <Link key={c.id} to={`/daily/${c.id}`} className="flex items-center gap-4 p-4 hover:bg-white/60 transition-colors" data-testid="past-contest-row">
                    <Trophy size={18} className={c.winner_user ? "text-primary" : "text-ink/45"} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{c.title}</div>
                      <div className="text-xs text-ink/62">{c.day}</div>
                    </div>
                    {c.winner_user ? <div className="flex items-center gap-2"><Avatar user={c.winner_user} size={28} /><span className="text-sm">@{c.winner_user.username}</span></div> : <span className="text-sm text-ink/56">Senza vincitore</span>}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function JoinDialog({ contest, open, onOpenChange, onJoined }: any) {
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ["mine-published"], queryFn: () => api.get("/me/contents", { params: { status: "published" } }).then((r) => r.data), enabled: open });
  const eligible = (data || []).filter((c: any) => c.category === contest.category && c.visibility === "public");
  const join = async (cid: string) => {
    try {
      await api.post(`/daily/${contest.id}/join`, { content_id: cid });
      toast.success("Sei in gara!");
      onOpenChange(false);
      onJoined();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="menu-panel rounded-3xl max-w-lg" data-testid="join-dialog">
        <DialogHeader><DialogTitle className="font-display">Partecipa a {contest.title}</DialogTitle></DialogHeader>
        <button className="btn btn-primary w-full" onClick={() => nav(`/pubblica?contest=${contest.id}&category=${contest.category}`)} data-testid="join-new-content-btn">Pubblica un contenuto nuovo</button>
        <div className="label text-ink/62 mt-2">Oppure scegli un contenuto già pubblicato in {contest.category_name}</div>
        {eligible.length === 0 ? <p className="text-sm text-ink/62">Nessun tuo contenuto pubblico in questa categoria.</p> : (
          <div className="grid grid-cols-3 gap-1 max-h-72 overflow-y-auto">
            {eligible.map((c: any) => (
              <button key={c.id} onClick={() => join(c.id)} className="relative aspect-[4/5] bg-white/45 overflow-hidden border border-transparent hover:border-primary" data-testid="join-existing-content">
                {c.media[0] && <img src={mediaUrl(c.media[0].kind === "video" ? c.media[0].thumb_url : c.media[0].url)} alt="" className="w-full h-full object-cover" />}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ContestPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [join, setJoin] = useState(false);
  const { data: c, isLoading } = useQuery({ queryKey: ["contest", id], queryFn: () => api.get(`/daily/${id}`).then((r) => r.data), refetchInterval: 30000 });
  if (isLoading || !c) return <div className="p-10"><Spinner /></div>;
  const open = c.status === "open";
  const accountAgeH = (Date.now() - new Date(user.created_at).getTime()) / 3600000;
  const canVote = user.email_verified && accountAgeH >= 24;
  return (
    <div data-testid="contest-page">
      <PageHeader kicker={c.category_name} title={c.title} back="/daily" />
      <div className="px-5 md:px-10 py-6 grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-8">
          <div className="surface p-5" data-testid="contest-format">
            <div className="label text-primary mb-2">Come si vince · {c.format_name}</div>
            <p className="text-sm text-ink/80 leading-relaxed">{c.format_how}</p>
            {c.criteria && <p className="text-sm text-ink/80 mt-2"><b>Criteri della giuria:</b> {c.criteria}</p>}
            {open && <p className="text-xs text-ink/60 mt-3">Classifica provvisoria: può cambiare fino alla chiusura. Il risultato finale viene fissato alla chiusura.</p>}
          </div>
          <div className="border border-ink/10 p-6 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="label text-ink/62 mb-1">{open ? "Si chiude tra" : "Stato"}</div>
              {open ? <Countdown to={c.ends_at} className="text-5xl font-bold" /> :
                <div className="font-display text-2xl font-bold" data-testid="contest-status">{c.status === "closed" ? "Conclusa" : "Chiusa senza vincitore"}</div>}
              {c.extended && <div className="text-primary text-sm mt-2">Prolungata di 24 ore per raggiungere i dati minimi</div>}
            </div>
            {open && (c.my_entry ? <div className="text-primary font-semibold" data-testid="contest-joined">Sei in gara</div> :
              <button className="btn btn-primary" onClick={() => setJoin(true)} data-testid="contest-join-btn">Partecipa</button>)}
          </div>
          {open && !canVote && <p className="text-sm text-ink/70 border-l-2 border-primary pl-3" data-testid="vote-eligibility-note">I tuoi Mi piace verranno contati nelle gare quando avrai l'email verificata e almeno 24 ore di anzianità.</p>}
          {c.my_stats && (
            <div className="border border-primary/50 p-5" data-testid="my-contest-stats">
              <div className="label text-primary mb-3">Il tuo contenuto</div>
              <div className="grid grid-cols-3 gap-4 font-mono">
                <div><div className="text-2xl font-bold">{c.my_stats.valid_views}/{c.min_valid_views}</div><div className="text-xs text-ink/62">viste valide</div></div>
                <div><div className="text-2xl font-bold">{c.my_stats.valid_likes}</div><div className="text-xs text-ink/62">Mi piace validi</div></div>
                <div><div className="text-2xl font-bold">{c.my_stats.qualified && c.format !== "curated" ? c.my_stats.score : "—"}</div><div className="text-xs text-ink/62">{c.format === "curated" ? "giudizio della giuria" : "punteggio"}</div></div>
              </div>
            </div>
          )}
          {c.winner_user && (
            <div className="border border-primary p-6 flex items-center gap-4" data-testid="contest-winner">
              <Trophy className="text-primary" size={32} />
              <div className="flex-1"><div className="label text-primary">Vincitore</div><Link to={`/u/${c.winner_user.username}`} className="font-display text-xl font-bold hover:text-primary">@{c.winner_user.username}</Link></div>
              <div className="font-mono text-2xl">{c.format === "curated" ? <span className="text-sm font-sans leading-tight whitespace-nowrap text-ink/70">Scelto dalla giuria</span> : c.winner.score}</div>
            </div>
          )}
          <section>
            <h2 className="label text-ink/62 mb-4">{open ? "Classifica provvisoria" : "Classifica finale"}</h2>
            {c.leaderboard.length === 0 ? <Empty testId="leaderboard-empty">Nessun contenuto ha ancora raggiunto {c.min_valid_views} visualizzazioni valide. La distribuzione è in corso.</Empty> : (
              <div className="border border-ink/10 divide-y divide-ink/10" data-testid="leaderboard">
                {c.leaderboard.map((r: any) => (
                  <div key={r.entry_id} className="flex items-center gap-4 p-3" data-testid="leaderboard-row">
                    <span className={`font-mono text-2xl font-bold w-10 ${r.rank === 1 ? "text-primary" : ""}`}>{r.rank}</span>
                    <div className="w-14"><Thumb c={r.content} /></div>
                    <div className="flex-1 min-w-0"><div className="text-sm truncate">{r.content.caption || "Senza titolo"}</div><div className="text-xs text-ink/62">@{r.user.username} · {r.valid_views} viste · {Math.round(r.completion * 100)}% completamento</div></div>
                    {c.format !== "curated" ? <span className="font-mono text-lg">{r.score}</span> : c.winner?.entry_id === r.entry_id && <span className="chip chip-accent">Scelto dalla giuria</span>}
                  </div>
                ))}
              </div>
            )}
            {c.pending.length > 0 && (
              <div className="mt-6">
                <div className="label text-ink/62 mb-3 flex items-center gap-2"><Eye size={12} />In distribuzione ({c.pending.length})</div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1">
                  {c.pending.map((p: any) => (
                    <div key={p.content.id} className="relative"><Thumb c={p.content} /><div className="absolute bottom-0 inset-x-0 h-1 bg-white/45"><div className="h-full bg-primary" style={{ width: `${Math.min(100, (p.valid_views / c.min_valid_views) * 100)}%` }} /></div></div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
        <aside className="space-y-4" data-testid="contest-rules">
          <h2 className="label text-ink/62 flex items-center gap-2"><Clock size={12} />Regole</h2>
          <ol className="space-y-3 text-sm text-ink/76 list-decimal pl-5">{c.rules.map((r: string) => <li key={r}>{r}</li>)}</ol>
        </aside>
      </div>
      <JoinDialog contest={c} open={join} onOpenChange={setJoin} onJoined={() => qc.invalidateQueries({ queryKey: ["contest", id] })} />
    </div>
  );
}
